<template>
  <AppModal
    :model-value="modelValue"
    :title="heading"
    size="lg"
    @update:model-value="(value: boolean) => emit('update:modelValue', value)"
  >
    <p v-if="isFiltered && grouped[0]?.weapons.length === 0" class="m-0 text-sm text-text-muted">{{ messages.picker.noWeaponsAvailable }}</p>

    <div
      v-for="bucket in grouped"
      :key="bucket.role"
      class="flex flex-col gap-2"
    >
      <span v-if="!isFiltered" class="font-mono text-xs tracking-[0.08em] uppercase text-text-muted">{{ weaponRoleLabel(bucket.role, messages.roles) }}</span>
      <div :class="isFiltered ? 'flex justify-center' : 'grid grid-cols-[repeat(auto-fill,minmax(128px,1fr))]'">
        <div :class="isFiltered ? 'inline-flex' : 'contents'">
          <button
            v-for="w in bucket.weapons"
            :key="w.slug"
            type="button"
            :disabled="forbiddenSet.has(w.slug)"
            :aria-disabled="forbiddenSet.has(w.slug)"
            :aria-label="weaponAriaLabel(w)"
            :class="[
              'flex flex-col items-center gap-2 p-2 bg-bg-surface border border-border rounded-md transition-[border-color,box-shadow] duration-120 ease-[cubic-bezier(0.4,0,0.2,1)]',
              forbiddenSet.has(w.slug)
                ? 'opacity-40 grayscale cursor-not-allowed'
                : 'cursor-pointer hover:border-accent hover:shadow-glow-accent',
              !forbiddenSet.has(w.slug) && currentSet.has(w.slug) ? 'border-accent shadow-[0_0_0_2px_color-mix(in_srgb,var(--color-accent)_30%,transparent)]' : null,
              isFiltered ? 'w-32' : null,
            ]"
            :title="weaponTitle(w)"
            @click="pick(w.slug)"
          >
            <span class="relative flex h-20 w-full items-start justify-center">
              <img
                :src="w.artwork"
                :alt="localizedWeaponName(w)"
                class="h-16 max-w-full object-contain"
              />
              <img
                :src="w.icon"
                alt=""
                aria-hidden="true"
                class="absolute bottom-0 left-1/2 size-7 -translate-x-1/2 object-contain"
              />
            </span>
            <span class="text-sm text-text-secondary text-center leading-[1.2] wrap-break-word hyphens-auto">{{ localizedWeaponName(w) }}</span>
          </button>
        </div>
      </div>
    </div>
  </AppModal>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { WEAPONS } from '@/data/weapons'
import { WeaponRole } from '@/types/weapon'
import type { Weapon } from '@/types/weapon'
import { AppModal } from '@/shared/ui'
import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { EMPTY_SLUGS } from '@/features/gvg-planner/lib/empty'
import { weaponDisplayName, weaponRoleLabel } from '@/features/gvg-planner/lib/roleLabels'
import { weaponsEn } from './locales/en.ts'
import { weaponsRu } from './locales/ru.ts'

// Bucket order — the picker renders HEAL first (most-desired on attack/defense
// teams) then TANK then DPS. Exhaustive over the current `WeaponRole` union.
const ROLE_ORDER: ReadonlyArray<WeaponRole> = [WeaponRole.Heal, WeaponRole.Tank, WeaponRole.Dps]

interface RoleBucket {
  readonly role: WeaponRole
  readonly weapons: ReadonlyArray<Weapon>
}

export interface WeaponPickerProps {
  /** Two-way bound open/close state (v-model). */
  readonly modelValue: boolean
  readonly heading?: string
  /** Slugs currently equipped on the target slot (for the "is-current" highlight). */
  readonly currentSlugs?: ReadonlyArray<string>
  /**
   * Slugs equipped on the partner slot (or partner position in a saved build).
   * These weapons are visually disabled and non-clickable — the user cannot
   * equip the same weapon in both slots of a pair.
   */
  readonly forbiddenSlugs?: ReadonlyArray<string>
  /**
   * Optional whitelist of weapon slugs. When provided (non-null and non-empty),
   * the picker shows ONLY these weapons in a single flat bucket (no role
   * grouping), in the order they appear in the array. When null / undefined /
   * empty, the picker falls back to the full catalog grid grouped by role.
   */
  readonly weaponSlugs?: ReadonlyArray<string> | null
}

const props = defineProps<WeaponPickerProps>()
const catalog = defineLocaleCatalog({ en: weaponsEn, ru: weaponsRu })
const messages = useLocaleMessages(catalog)

const forbiddenSet = computed<ReadonlySet<string>>(
  () => new Set(props.forbiddenSlugs ?? EMPTY_SLUGS),
)

const currentSet = computed<ReadonlySet<string>>(
  () => new Set(props.currentSlugs ?? EMPTY_SLUGS),
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'pick', slug: string): void
}>()

/**
 * Visible heading for the dialog. The catalog supplies the standalone fallback
 * while contextual callers can provide a more specific heading.
 */
const heading = computed<string>(() => props.heading ?? messages.value.picker.chooseWeapon)

const grouped = computed<ReadonlyArray<RoleBucket>>(() => {
  const whitelist = props.weaponSlugs
  if (whitelist && whitelist.length > 0) {
    const slugSet = new Set<string>(whitelist)
    const ordered = WEAPONS.filter((w) => slugSet.has(w.slug))
    return [
      {
        role: WeaponRole.Tank,
        weapons: ordered,
      },
    ]
  }
  return ROLE_ORDER.map((role) => ({
    role,
    weapons: WEAPONS.filter((w) => w.role === role),
  }))
})

const isFiltered = computed<boolean>(
  () => props.weaponSlugs !== null && props.weaponSlugs !== undefined && props.weaponSlugs.length > 0,
)

function localizedWeaponName(weapon: Weapon): string {
  return weaponDisplayName(weapon.slug, weapon.name, messages.value.names)
}

function weaponAriaLabel(weapon: Weapon): string {
  const label = messages.value.picker.selectWeapon(
    localizedWeaponName(weapon),
    weaponRoleLabel(weapon.role, messages.value.roles),
  )
  return forbiddenSet.value.has(weapon.slug)
    ? `${label}${messages.value.picker.unavailableSuffix}`
    : label
}

function weaponTitle(weapon: Weapon): string {
  const label = messages.value.picker.weaponTitle(
    localizedWeaponName(weapon),
    weaponRoleLabel(weapon.role, messages.value.roles),
  )
  return forbiddenSet.value.has(weapon.slug)
    ? `${label}${messages.value.picker.unavailableSuffix}`
    : label
}

function pick(slug: string): void {
  emit('pick', slug)
  emit('update:modelValue', false)
}
</script>
